import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useSession } from "../session/SessionContext";
import { EmptyState, ErrorState, PermissionDeniedState } from "../components/states";
import { UsageLoading } from "../components/consumption/UsageLoading";
import { ConsumptionSummary } from "../components/consumption/ConsumptionSummary";
import { useSelectedPeriod } from "../hooks/useSelectedPeriod";
import { previousMonthStart } from "../components/consumption/usagePace";
import { fetchPersonUsage, type PersonUsageResponse } from "../api/reporting";
import type { PersonSummary } from "../api/entitlementAdmin";
import { getPerson } from "../api/people";
import { ManageIcon } from "../components/shared/ManageIcon";
import { unchangedOr, usePolling } from "../hooks/usePolling";
import styles from "./PersonUsagePage.module.css";

const OthersConsumptionPermission = "consumption.read.others";

type PageState = { kind: "loading" } | { kind: "loaded"; response: PersonUsageResponse } | { kind: "denied" } | { kind: "error"; message: string };

/** What the person endpoint said, in `backTo="history"` mode only: whether to offer Manage, and whether the person is inactive. */
type PersonState = { kind: "loading" } | { kind: "known"; person: PersonSummary } | { kind: "unknown" };

/** The administration page for a person. */
function manageHref(personId: string): string {
  return `/admin/users/${encodeURIComponent(personId)}`;
}

/** Does nothing: on someone else's usage the threshold banner is not shown, so there is nothing to acknowledge. */
function ignoreThreshold(): void {
  // Only the person themself can acknowledge their thresholds.
}

/**
 * Someone else's "My Usage", for a manager who owns a group they are in: the same page the person sees, reached from the
 * people on a group's page, read only. The server decides who may be seen; anyone else is reported as not permitted.
 *
 * `backTo` picks the way back. `"context"` (the default) links to the group or the Organisation page the person was opened
 * from. `"history"` returns to the previous page in this tab, or links to Home when there is none.
 */
export function PersonUsagePage({ backTo = "context" }: { backTo?: "context" | "history" }): React.JSX.Element {
  const { t } = useTranslation();
  const { snapshot } = useSession();
  const { groupId = "", personId = "" } = useParams();
  const [state, setState] = useState<PageState>({ kind: "loading" });
  const [periodStart] = useSelectedPeriod();
  const hasPermission = snapshot.status === "signed-in" && snapshot.permissions.includes(OthersConsumptionPermission);
  const neutral = backTo === "history";
  const [personState, setPersonState] = useState<PersonState>({ kind: "loading" });

  // A quiet load refreshes the figures in place, without the loading layout, and keeps them if it fails.
  const load = useCallback(
    async (signal?: AbortSignal, quiet = false) => {
      if (!quiet) {
        setState({ kind: "loading" });
      }
      const result = await fetchPersonUsage(personId, periodStart, signal);
      if (result.kind === "aborted" || (quiet && result.kind !== "ok")) {
        return;
      }
      switch (result.kind) {
        case "denied":
          setState({ kind: "denied" });
          return;
        case "error":
          setState({ kind: "error", message: result.message });
          return;
        case "ok": {
          const next: PageState = { kind: "loaded", response: result.response };
          setState((previous) => unchangedOr(previous, next));
        }
      }
    },
    [personId, periodStart],
  );

  // Opened from far down a group's list of people, the page starts at its top, with whose usage it is. The shell scrolls its
  // main area, not the window.
  const pageRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const scroller = pageRef.current?.closest("main");
    if (scroller && typeof scroller.scrollTo === "function") {
      scroller.scrollTo({ top: 0 });
    }
  }, [personId]);

  useEffect(() => {
    const controller = new AbortController();
    if (hasPermission) {
      void load(controller.signal);
    }
    return () => controller.abort();
  }, [hasPermission, load]);

  // The neutral page also asks who the person is to the caller: whether Manage is offered, and whether they are inactive (and so have no
  // usage to load). Any failure, including the one 404 every refusal shares, leaves the page as it was.
  useEffect(() => {
    if (!neutral || !hasPermission) return;
    const controller = new AbortController();
    setPersonState({ kind: "loading" });
    void getPerson(personId, controller.signal).then((outcome) => {
      if (outcome.kind === "aborted") return;
      setPersonState(outcome.kind === "ok" ? { kind: "known", person: outcome.person } : { kind: "unknown" });
    });
    return () => controller.abort();
  }, [neutral, hasPermission, personId]);

  usePolling(() => load(undefined, true), hasPermission && state.kind === "loaded");

  // The group's name comes with the link from its people list; a page opened directly says "the group".
  const location = useLocation();
  const groupName = (location.state as { groupName?: string } | null)?.groupName;

  return (
    <div ref={pageRef}>
      {backTo === "history" ? (
        <div className={styles.topRow}>
          <HistoryBackLink />
          {personState.kind === "known" && personState.person.canAdminister && personState.person.status !== "Inactive" && (
            <Link to={manageHref(personId)} className={styles.back}>
              <ManageIcon className={styles.manageIcon} />
              {t("personUsage.manage")}
            </Link>
          )}
        </div>
      ) : (
        /* Opened from a group's people it returns there; opened from the Organisation page (no group), to its people. */
        <Link to={groupId ? `/my-groups/${encodeURIComponent(groupId)}#group-people` : "/organization#organisation-person"} className={styles.back}>
          <BackIcon />
          {groupId === "" ? t("personUsage.backToOrganisation") : groupName ? t("personUsage.backTo", { name: groupName }) : t("personUsage.back")}
        </Link>
      )}
      <PersonUsageBody hasPermission={hasPermission} state={state} onRetry={() => void load()} person={neutral ? personState : null} />
    </div>
  );
}

function BackIcon(): React.JSX.Element {
  return (
    <svg className={styles.backIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M15 18l-6-6 6-6" />
    </svg>
  );
}

/**
 * Goes back to the previous page in this tab. React Router gives the first entry of a tab the key "default", so a page
 * opened directly (a bookmark or a pasted link) has nothing to go back to and links to Home instead.
 */
function HistoryBackLink(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  if (location.key === "default") {
    return (
      <Link to="/" className={styles.back}>
        <BackIcon />
        {t("personUsage.backHome")}
      </Link>
    );
  }

  return (
    <button type="button" className={`${styles.back} ${styles.backButton}`} onClick={() => void navigate(-1)}>
      <BackIcon />
      {t("personUsage.backPrevious")}
    </button>
  );
}

function PersonUsageBody({
  hasPermission,
  state,
  onRetry,
  person,
}: {
  hasPermission: boolean;
  state: PageState;
  onRetry: () => void;
  /** Present on the neutral page only: what the person endpoint said, used to tell an inactive person from one who cannot be seen. */
  person: PersonState | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const denied = <PermissionDeniedState title={t("personUsage.denied.title")} description={t("personUsage.denied.description")} />;

  if (!hasPermission) {
    return denied;
  }

  switch (state.kind) {
    case "loading":
      return <UsageLoading />;
    case "denied":
      if (person?.kind === "loading") {
        return <UsageLoading />;
      }
      if (person?.kind === "known" && person.person.status === "Inactive") {
        return (
          <EmptyState title={t("personUsage.inactive.title", { email: person.person.email })} description={t("personUsage.inactive.description")}>
            {person.person.canAdminister && (
              <Link to={manageHref(person.person.id)} className={styles.back}>
                <ManageIcon className={styles.manageIcon} />
                {t("personUsage.manage")}
              </Link>
            )}
          </EmptyState>
        );
      }
      return denied;
    case "error":
      return <UsageLoading error={<ErrorState title={t("personUsage.errorTitle")} description={state.message} onRetry={onRetry} />} />;
    case "loaded": {
      const { consumption, history, effectiveAllowanceUsd, email } = state.response;
      const previousStart = previousMonthStart(consumption.periodStart);
      return consumption.hasAnyData ? (
        <ConsumptionSummary
          response={consumption}
          onDismissThreshold={ignoreThreshold}
          dismissingThreshold={false}
          effectiveAllowanceUsd={effectiveAllowanceUsd}
          previousPeriod={history.periods.find((period) => period.periodStart === previousStart) ?? null}
          subjectName={email}
        />
      ) : (
        <EmptyState title={t("personUsage.emptyTitle")} description={t("personUsage.emptyDescription")} />
      );
    }
  }
}
